import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';

import { DATE_FORMAT } from '@/constants';
import { Getter } from '@/decorators';
import { HRMAccountTeamDTO, ProjectDTO, ProjectManager, SelectionOptions, UserDTO } from '@/models';
import { formatDateToDisplay, formatDateToLocalDateTime } from '@/helpers';
import { projectTypeMapping } from '@/services';

import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import PageLoader from '@/components/common/loader/page-loader/PageLoader.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import Avatar from '@/components/common/avatar/Avatar.vue';

import ProjectInformationForm from '@/components/pages/project-evaluation/project-information/project-information-form/ProjectInformationForm.vue';
import ProjectInformationMixin from '@/components/pages/project-evaluation/project-information/project-information-mixin';

import ManagerListPopup from './project-manager-information/manager-list-popup/ManagerListPopup.vue';
import ProjectManagerInformation from './project-manager-information/ProjectManagerInformation.vue';

@Component({
  components: {
    Avatar,
    ConfirmationDialog,
    PageLoader,
    ProjectInformationForm,
    UserInformation,
    ManagerListPopup,
    ProjectManagerInformation
  }
})
export default class ProjectInformation extends mixins(ProjectInformationMixin) {
  @Prop() projectInfo: ProjectDTO;
  @Prop() currentUser: UserDTO;
  @Prop({ default: false }) canEdit: boolean;

  @Getter('project/managers')
  managers: ProjectManager[];

  private isEditMode = false;
  private projectTypeMapping = projectTypeMapping;
  private userOptions = [] as SelectionOptions;
  private dirtyCheckDialogShown = false;
  private saveLoading = false;
  private hrmProjectName = '';

  @Watch('isEditMode')
  setIsProjectEditing(): void {
    this.$store.commit('project/setIsProjectEditing', this.isEditMode);
  }

  @Watch('projectInfo')
  onHRMProjectLinkedChange(): void {
    this.setHRMProjectName();
  }

  async created(): Promise<void> {
    this.userOptions = this.buildUserOptions();
    await this.buildFormData();
    this.setHRMProjectName();
  }

  private setHRMProjectName(): void {
    if (this.projectInfo.hrmProjectId) {
      this.setHRMProjectNameByHRMProjectId(this.projectInfo.hrmProjectId);
    }
  }

  async buildFormData(): Promise<void> {
    this.projectFormData = await this.buildProjectData(this.projectInfo);
  }

  getDateString(isoString: string): string {
    return formatDateToDisplay(isoString);
  }

  async setHRMProjectNameByHRMProjectId(hrmProjectId: number): Promise<void> {
    const hrmProjects =
      this.$store.getters['project/hrmProjects']?.length > 0
        ? this.$store.getters['project/hrmProjects']
        : await this.$store.dispatch('project/fetchHRMProjects');
    const project = hrmProjects?.find((p: HRMAccountTeamDTO) => p.projectId === hrmProjectId);
    this.hrmProjectName = `${project?.clientName} - ${project?.projectName}`;
  }

  async save(): Promise<void> {
    const isDataValid = this.validateProjectData();
    if (isDataValid) {
      this.saveLoading = true;
      const projectData = {
        ...this.projectFormData,
        startDate: formatDateToLocalDateTime(this.projectFormData.startDate),
        endDate: formatDateToLocalDateTime(this.projectFormData.endDate)
      };
      await this.$store.dispatch('project/asyncUpdateProjectInformation', projectData);
      await this.buildFormData();
      this.isEditMode = false;
      this.saveLoading = false;
    }
  }

  cancel(): void {
    if (this.isDirty()) {
      this.dirtyCheckDialogShown = true;
    } else {
      this.isEditMode = false;
    }
  }

  isOldProject(): boolean {
    return this.projectInfo.projectAssessments.length !== 0;
  }

  isDirty(): boolean {
    const { projectFormData, projectInfo } = this;
    return (
      projectFormData.name !== projectInfo.name ||
      projectFormData.description !== projectInfo.description ||
      parseInt(projectFormData.sponsorId, 10) !== projectInfo.sponsor.id ||
      parseInt(projectFormData.managerId, 10) !== projectInfo.manager.id ||
      projectFormData.projectType !== projectInfo.projectType ||
      this.isDateDirty(projectFormData.startDate, projectInfo.startDate) ||
      this.isDateDirty(projectFormData.endDate, projectInfo.endDate)
    );
  }

  isDateDirty(date: string, originalDate: string): boolean {
    if (!date && !originalDate) {
      return false;
    }
    return moment.utc(date).format(DATE_FORMAT.STORED) !== originalDate;
  }

  onDirtyDialogConfirmed(): void {
    this.dirtyCheckDialogShown = false;
    this.isEditMode = false;
    this.buildFormData();
  }

  changeToEditMode(): void {
    this.isEditMode = true;
  }
}
